import {
  decodedValueEqual,
  decodeTaggedProps,
  editableScalarKind,
  scalarEditText,
  type EditableScalarKind,
  type EntityResolution,
  type FieldResolution,
  type SplitResolution,
  type TaggedValue
} from '@axilon-apps/domains-ontology-ui-state'
import { labelsDiverge, renderValue, renderValueDisambiguated } from './entity-display'

export type ConflictSide = 'ours' | 'theirs'

/** Why a conflict can only be resolved whole-entity. */
export type ConflictStructuralReason = 'removed' | 'labels' | 'identity' | 'undecodable'

export interface ConflictSpec {
  /**
   * Resolve this conflict whole-entity only — a side removed the entity, the
   * two sides' labels diverge (nodes), the identity triple diverges (rels),
   * or a side's payload cannot be decoded. Per-field picking does not apply;
   * the column headers are the only control.
   */
  wholeEntityOnly: boolean
  /** Which structural case applies, when wholeEntityOnly. */
  reason?: ConflictStructuralReason
  /** Property keys where the two sides differ — each needs a side to resolve. */
  fields: string[]
}

// A cell's state for one property on one side: the side removed the entity
// (`was` = the base value it drops, rendered struck through), the property is
// absent on that side, or it holds a value.
export type ConflictCell =
  | { kind: 'removed'; was?: string }
  | { kind: 'absent' }
  | { kind: 'value'; text: string }

export interface ConflictPropertyRow {
  key: string
  base: ConflictCell
  ours: ConflictCell
  theirs: ConflictCell
  differs: boolean
  editKind: EditableScalarKind | null
  seedText: string
}

type DecodedProps = ReturnType<typeof decodeTaggedProps>
// A side's decoded properties, the fact it was removed (null), or that its
// payload could not be decoded.
type SideProps = { ok: true; props: DecodedProps } | { ok: false } | null

/** The entity shape a conflict side carries — rel identity fields optional. */
type ConflictSideEntity = {
  id?: string
  labels?: string[]
  properties?: Record<string, unknown>
  type?: string
  startId?: string
  endId?: string
} | null

/**
 * Classify a conflict for resolution: whether it can only be resolved
 * whole-entity (one side removed it, the labels/identity diverge, or a side
 * is undecodable — all of which the per-field assembler rejects server-side),
 * and which property keys actually differ between the two sides.
 *
 * `serverFields` is the engine's authoritative per-field set carried on the
 * conflict wire payload — when present (non-null) it wins outright, so the
 * resolver can never disagree with the merge assembler about what needs a
 * pick. The structural fallback (payloads persisted before the server
 * emitted it) compares decoded values structurally — never rendered text,
 * which masks type-only differences.
 */
export function conflictSpec(
  ours: ConflictSideEntity,
  theirs: ConflictSideEntity,
  serverFields?: string[] | null
): ConflictSpec {
  if (!ours || !theirs) return { wholeEntityOnly: true, reason: 'removed', fields: [] }
  if (labelsDiverge(ours.labels, theirs.labels)) {
    return { wholeEntityOnly: true, reason: 'labels', fields: [] }
  }
  if (relIdentityDiverges(ours, theirs)) {
    return { wholeEntityOnly: true, reason: 'identity', fields: [] }
  }
  if (serverFields != null) {
    return { wholeEntityOnly: false, fields: [...serverFields].sort() }
  }
  const o = decodeSide(false, ours.properties)
  const t = decodeSide(false, theirs.properties)
  if (!o?.ok || !t?.ok) return { wholeEntityOnly: true, reason: 'undecodable', fields: [] }
  return { wholeEntityOnly: false, fields: differingFields(o.props, t.props) }
}

// A relationship's identity triple diverging between sides (possible only
// under legacy opaque ids) is whole-entity territory, like label divergence.
function relIdentityDiverges(ours: ConflictSideEntity, theirs: ConflictSideEntity): boolean {
  if (!ours || !theirs) return false
  return ours.type !== theirs.type || ours.startId !== theirs.startId || ours.endId !== theirs.endId
}

// Property keys whose decoded values differ structurally (id excluded).
function differingFields(
  ours: ReturnType<typeof decodeTaggedProps>,
  theirs: ReturnType<typeof decodeTaggedProps>
): string[] {
  const keys = new Set<string>()
  for (const side of [ours, theirs]) {
    for (const k of Object.keys(side)) if (k !== 'id') keys.add(k)
  }
  return [...keys].sort().filter((key) => {
    const oHas = key in ours
    const tHas = key in theirs
    if (oHas !== tHas) return true
    return oHas && !decodedValueEqual(ours[key], theirs[key])
  })
}

export function cellEq(a: ConflictCell, b: ConflictCell): boolean {
  return a.kind === b.kind && (a.kind !== 'value' || a.text === (b as { text: string }).text)
}

function decodeSide(absent: boolean, props?: Record<string, unknown>): SideProps {
  if (absent) return null
  try {
    return { ok: true, props: decodeTaggedProps(props ?? {}) }
  } catch {
    return { ok: false }
  }
}

function cellFor(side: SideProps, key: string): ConflictCell {
  if (side === null) return { kind: 'removed' }
  if (!side.ok) return { kind: 'value', text: '⚠ undecodable' }
  if (!(key in side.props)) return { kind: 'absent' }
  return { kind: 'value', text: renderValue(side.props[key]) }
}

// The decoded value for one property on one side, when that side carries it —
// the input the content-value editor reads its kind and seed from.
function decodedScalar(side: SideProps, key: string): DecodedProps[string] | undefined {
  if (side === null || !side.ok || !(key in side.props)) return undefined
  return side.props[key]
}

export function buildConflictPropertyRows({
  base,
  baseAbsent,
  ours,
  oursAbsent,
  theirs,
  theirsAbsent,
  conflictingFields
}: {
  base?: Record<string, unknown>
  baseAbsent: boolean
  ours?: Record<string, unknown>
  oursAbsent: boolean
  theirs?: Record<string, unknown>
  theirsAbsent: boolean
  /**
   * The authoritative per-field conflict set (`ConflictSpec.fields`) when
   * per-field resolution applies. Rows mark `differs` from this — never from
   * comparing display text, which masks type-only differences — so the
   * pickable rows, the submit gating, and the server's assembler always
   * agree. Undefined for a whole-entity-only conflict (a side removed the
   * entity, labels diverge): rows aren't individually pickable there, so
   * `differs` falls back to a structural side-by-side comparison purely for
   * display (a removed side's dropped values must still read as different).
   */
  conflictingFields?: string[]
}): ConflictPropertyRow[] {
  const sides = {
    base: decodeSide(baseAbsent, base),
    ours: decodeSide(oursAbsent, ours),
    theirs: decodeSide(theirsAbsent, theirs)
  }
  // Union of property keys across the three versions, minus `id` (the entity
  // identity — shown in the header, not as a diffable field).
  const keys = new Set<string>(conflictingFields ?? [])
  for (const side of [sides.base, sides.ours, sides.theirs]) {
    if (side && side.ok) {
      for (const k of Object.keys(side.props)) if (k !== 'id') keys.add(k)
    }
  }
  return [...keys].sort().map((key) => {
    const b = cellFor(sides.base, key)
    let o = cellFor(sides.ours, key)
    let t = cellFor(sides.theirs, key)
    const differs = conflictingFields
      ? conflictingFields.includes(key)
      : cellsDifferStructurally(sides.ours, sides.theirs, o, t, key)
    // A conflicting field whose two sides render identically (a type-only
    // difference — int 5 vs float 5.0) re-renders with the value kind spelled
    // out, so the user can see what they are choosing between.
    if (differs && o.kind === 'value' && t.kind === 'value' && o.text === t.text) {
      o = disambiguatedCell(sides.ours, key) ?? o
      t = disambiguatedCell(sides.theirs, key) ?? t
    }
    // A removed side shows the base value it drops, struck through.
    const baseText = b.kind === 'value' ? b.text : undefined
    const dropped = (c: ConflictCell): ConflictCell =>
      c.kind === 'removed' && baseText !== undefined ? { kind: 'removed', was: baseText } : c
    // The content editor needs a scalar to edit; take the kind + seed from
    // whichever side carries the property (a non-scalar leaves edit disabled).
    const decoded = decodedScalar(sides.ours, key) ?? decodedScalar(sides.theirs, key)
    const editKind = decoded !== undefined ? editableScalarKind(decoded) : null
    const seedText = decoded !== undefined ? scalarEditText(decoded) : ''
    return { key, base: b, ours: dropped(o), theirs: dropped(t), differs, editKind, seedText }
  })
}

// The type-annotated cell for one side's property, when that side decodes and
// carries it (the only case where disambiguation applies).
function disambiguatedCell(side: SideProps, key: string): ConflictCell | null {
  if (side === null || !side.ok || !(key in side.props)) return null
  return { kind: 'value', text: renderValueDisambiguated(side.props[key]) }
}

// The display-difference fallback for whole-entity-only conflicts: cell kinds
// diverge (removed / absent vs a value), or two carried values differ
// structurally. An undecodable side compares by display text — the only
// content it has.
function cellsDifferStructurally(
  ours: SideProps,
  theirs: SideProps,
  o: ConflictCell,
  t: ConflictCell,
  key: string
): boolean {
  if (o.kind !== t.kind) return true
  if (o.kind !== 'value') return false
  const ov = decodedScalar(ours, key)
  const tv = decodedScalar(theirs, key)
  if (ov !== undefined && tv !== undefined) return !decodedValueEqual(ov, tv)
  return o.text !== (t as { text: string }).text
}

// The display / seed text of a chosen content value (tagged scalar).
export function taggedScalarText(value: TaggedValue): string {
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return String(value)
  return value.v
}

// The "split into two nodes" choice, when the resolution is a split rather than
// a side / per-field map. Shape-checked so a per-field map with a field literally
// named "split" is not mistaken for one.
export function conflictSplit(choice: EntityResolution | undefined): SplitResolution | undefined {
  if (!choice || typeof choice !== 'object') return undefined
  const s = (choice as { split?: unknown }).split
  if (s && typeof s === 'object' && 'keep' in s && 'newId' in s) {
    return s as SplitResolution
  }
  return undefined
}

// The chosen side for one field: a whole-entity string applies to every
// field; a per-field map carries individual picks; a content value, a split, or
// nothing chosen yet is undefined (no side highlighted).
export function conflictFieldSide(
  choice: EntityResolution | undefined,
  key: string
): ConflictSide | undefined {
  if (choice === 'ours' || choice === 'theirs') return choice
  if (choice && typeof choice === 'object' && !conflictSplit(choice)) {
    const fr = (choice as Record<string, FieldResolution>)[key]
    return fr === 'ours' || fr === 'theirs' ? fr : undefined
  }
  return undefined
}

// The chosen content value for one field, when the per-field resolution is a
// corrected value rather than a side.
export function conflictFieldValue(
  choice: EntityResolution | undefined,
  key: string
): TaggedValue | undefined {
  if (choice && typeof choice === 'object' && !conflictSplit(choice)) {
    const fr = (choice as Record<string, FieldResolution>)[key]
    if (fr !== null && typeof fr === 'object' && 'value' in fr) return fr.value
  }
  return undefined
}

// A column header reads as selected when every conflicting field resolves to
// it (whole-entity-only: when the whole-entity side is it).
export function conflictColumnSelected({
  choice,
  conflictingKeys,
  wholeEntityOnly,
  side
}: {
  choice: EntityResolution | undefined
  conflictingKeys: string[]
  wholeEntityOnly: boolean
  side: ConflictSide
}): boolean {
  return wholeEntityOnly || conflictingKeys.length === 0
    ? choice === side
    : conflictingKeys.every((k) => conflictFieldSide(choice, k) === side)
}
